import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PlanFeature {
  id: string;
  feature_key: string;
  enabled: boolean;
  limit_value: number | null;
}

interface Plan {
  id: string;
  tier: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  trial_days: number;
  is_active: boolean;
  features: PlanFeature[];
}

export function useAdminPlans() {
  return useQuery({
    queryKey: ["admin-plans"],
    queryFn: async () => {
      const { data: plans, error: plansError } = await supabase
        .from("plans")
        .select("*")
        .order("price_cents", { ascending: true });

      if (plansError) throw plansError;

      const { data: features, error: featuresError } = await supabase
        .from("plan_features")
        .select("*");

      if (featuresError) throw featuresError;

      const plansWithFeatures: Plan[] = (plans ?? []).map((plan) => ({
        ...plan,
        features: (features ?? []).filter((f) => f.plan_id === plan.id),
      }));

      return plansWithFeatures;
    },
    staleTime: 60 * 1000,
  });
}

export function useUpdatePlanFeature() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      feature_id: string;
      enabled: boolean;
      limit_value: number | null;
    }) => {
      const { data, error } = await supabase.functions.invoke("admin-api", {
        body: {
          action: "update_plan_feature",
          feature_id: params.feature_id,
          enabled: params.enabled,
          limit_value: params.limit_value,
        },
      });

      if (error) throw new Error(error.message || "Erro ao atualizar feature");
      if (!data.success) throw new Error(data.error || "Erro desconhecido");

      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Feature atualizada com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
    },
    onError: (error: Error) => {
      toast({ title: error.message || "Erro ao atualizar feature", variant: "destructive" });
    },
  });
}
